import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { STLLoader, OBJLoader, PLYLoader } from 'three-stdlib';

// Calculate actual mesh volume using signed volume of tetrahedra
const calculateMeshVolume = (geometry) => {
  const positions = geometry.attributes.position.array;
  let volume = 0;
  
  // Calculate volume using divergence theorem
  // For each triangle, calculate signed volume of tetrahedron from origin
  for (let i = 0; i < positions.length; i += 9) {
    const v1 = new THREE.Vector3(positions[i], positions[i+1], positions[i+2]);
    const v2 = new THREE.Vector3(positions[i+3], positions[i+4], positions[i+5]);
    const v3 = new THREE.Vector3(positions[i+6], positions[i+7], positions[i+8]);
    
    // Signed volume of tetrahedron = (v1 · (v2 × v3)) / 6
    const cross = new THREE.Vector3().crossVectors(v2, v3);
    volume += v1.dot(cross);
  }
  
  return Math.abs(volume / 6);
};

// Calculate actual mesh surface area
const calculateMeshSurfaceArea = (geometry) => {
  const positions = geometry.attributes.position.array;
  let area = 0;
  
  // Calculate area of each triangle
  for (let i = 0; i < positions.length; i += 9) {
    const v1 = new THREE.Vector3(positions[i], positions[i+1], positions[i+2]);
    const v2 = new THREE.Vector3(positions[i+3], positions[i+4], positions[i+5]);
    const v3 = new THREE.Vector3(positions[i+6], positions[i+7], positions[i+8]);
    
    // Area = 0.5 * ||(v2-v1) × (v3-v1)||
    const edge1 = new THREE.Vector3().subVectors(v2, v1);
    const edge2 = new THREE.Vector3().subVectors(v3, v1);
    const cross = new THREE.Vector3().crossVectors(edge1, edge2);
    area += cross.length() * 0.5;
  }
  
  return area;
};

// Server conversion utility with extended timeout for STEP files
const convertFileToSTL = async (file, fileType) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const endpoint = fileType === '3mf' ? '/api/convert/3mf-to-stl' : '/api/convert/step-to-stl';
  
  // Create an AbortController with longer timeout for STEP files (120 seconds)
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), fileType === 'step' ? 120000 : 60000);
  
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      body: formData,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Server conversion failed: ${errorText || response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Conversion failed on server');
    }
    
    // Convert hex STL data back to binary
    const stlHex = result.stl_data;
    const stlBuffer = new ArrayBuffer(stlHex.length / 2);
    const stlView = new Uint8Array(stlBuffer);
    
    for (let i = 0; i < stlHex.length; i += 2) {
      stlView[i / 2] = parseInt(stlHex.substring(i, i + 2), 16);
    }
    
    // Parse converted STL
    const stlLoader = new STLLoader();
    return stlLoader.parse(stlBuffer);
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`${fileType.toUpperCase()} conversion timed out. Please try with a smaller file or contact support.`);
    }
    throw error;
  }
};

const ThreeDPreview = ({ file, cadFileUrl, onAnalysis, className = '', material, strength, quality, units = 'mm' }) => {
  const canvasRef = useRef(null);
  const [previewState, setPreviewState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const [isStepFile, setIsStepFile] = useState(false);

  // Unit conversion functions
  const convertVolumeToCm3 = (volume, fileUnits) => {
    const conversionFactors = {
      'mm': 0.001,      // 1 mm³ = 0.001 cm³
      'cm': 1.0,        // 1 cm³ = 1 cm³
      'm': 1000000.0,   // 1 m³ = 1,000,000 cm³
      'in': 16.387064,  // 1 in³ = 16.387064 cm³
      'ft': 28316.8466  // 1 ft³ = 28,316.8466 cm³
    };
    return volume * (conversionFactors[fileUnits] || 1.0);
  };

  const convertAreaToCm2 = (area, fileUnits) => {
    const conversionFactors = {
      'mm': 0.01,       // 1 mm² = 0.01 cm²
      'cm': 1.0,        // 1 cm² = 1 cm²
      'm': 10000.0,     // 1 m² = 10,000 cm²
      'in': 6.4516,     // 1 in² = 6.4516 cm²
      'ft': 929.0304    // 1 ft² = 929.0304 cm²
    };
    return area * (conversionFactors[fileUnits] || 1.0);
  };

  // Material densities (g/cm³)
  const getMaterialDensity = (materialType) => {
    const densities = {
      'PLA': 1.24,
      'ABS': 1.04,
      'PETG': 1.27,
      'TPU': 1.21,
      'WOOD_FILL': 1.15,
      'METAL_FILL': 1.50,
      'CARBON_FIBER': 1.30
    };
    return densities[materialType] || 1.24; // Default to PLA
  };

  // Infill percentages
  const getInfillPercentage = (strengthType) => {
    const infills = {
      'COSMETIC': 0.17,
      'Cosmetic Only': 0.17,      // 17%
      'REINFORCED': 0.27,
      'Reinforced': 0.27,    // 27%
      'FUNCTIONAL': 0.58,
      'Functional': 0.58     // 58%
    };
    return infills[strengthType] || 0.27; // Default to REINFORCED
  };

  // Calculate material weight
  const calculateMaterialWeight = (volume, materialType, strengthType) => {
    if (!volume || !materialType || !strengthType) return null;
    const density = getMaterialDensity(materialType);
    const infill = getInfillPercentage(strengthType);
    return volume * density * infill;
  };

  // Quality adjustment factors
  const getQualityAdjustmentFactor = (qualityType) => {
    const factors = {
      'Concept': 1.0,
      'Prototype': 1.35,
      'Test/Demo': 1.8,
      'End Use': 2.4,
      'Display': 4.0
    };
    return factors[qualityType] || 1.35;
  };

  // Calculate weight adjustment factor
  const calculateWeightAdjustmentFactor = (volumeAreaRatio) => {
    return -0.0865 * volumeAreaRatio + 4.3115;
  };

  // Calculate actual print weight
  const calculateActualPrintWeight = (requiredWeight, weightAdjustmentFactor) => {
    return requiredWeight * weightAdjustmentFactor;
  };

  // Calculate cost adjustment factor
  const calculateCostAdjustmentFactor = (actualPrintWeight) => {
    const factor = -0.0008 * actualPrintWeight + 9.7742;
    return Math.max(2.0, factor);  // Minimum of 2.0
  };

  // Calculate complexity adjustment factor
  const calculateComplexityAdjustmentFactor = (complexityPercentage) => {
    return 1 + complexityPercentage;
  };

  useEffect(() => {
    if (file) {
      // Use direct file (quote tool usage)
      loadModelFromFile();
    } else if (cadFileUrl) {
      // Load from URL (marketplace usage)
      loadModelFromUrl();
    }
  }, [file, cadFileUrl]);

  // Recalculate volume and surface area when units change
  useEffect(() => {
    if (fileInfo && fileInfo.rawVolume && fileInfo.rawSurfaceArea) {
      // Recalculate with new units
      const newVolume = convertVolumeToCm3(fileInfo.rawVolume, units);
      const newSurfaceArea = convertAreaToCm2(fileInfo.rawSurfaceArea, units);
      
      // Update fileInfo with converted values
      setFileInfo(prev => ({
        ...prev,
        volume: newVolume,
        surfaceArea: newSurfaceArea
      }));
      
      console.log(`Units changed to ${units}: Volume=${newVolume.toFixed(2)} cm³, Surface Area=${newSurfaceArea.toFixed(2)} cm²`);
    }
  }, [units]);

  const loadModelFromUrl = async () => {
    if (!cadFileUrl || !canvasRef.current) return;

    setPreviewState('loading');
    setErrorMessage('');

    try {
      // Fetch the CAD file from URL
      const response = await fetch(cadFileUrl);
      if (!response.ok) {
        throw new Error(`Failed to load CAD file: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const filename = cadFileUrl.split('/').pop() || 'model.stl';
      
      // Create a proper Blob for file conversion (needed for STEP/3MF)
      const blob = new Blob([arrayBuffer]);
      const file = new File([blob], filename, { type: 'application/octet-stream' });
      
      // Add arrayBuffer method for direct parsing compatibility
      file.arrayBuffer = () => Promise.resolve(arrayBuffer);
      
      await loadModel(file);
      
    } catch (error) {
      console.error('Failed to load CAD file from URL:', error);
      setErrorMessage(`Failed to load 3D model: ${error.message}`);
      setPreviewState('error');
    }
  };

  const loadModelFromFile = async () => {
    if (!file) return;
    await loadModel(file);
  };

  const loadModel = async (fileObj) => {
      setPreviewState('loading');
      setErrorMessage('');

      try {
        // Setup Three.js scene
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf8f9fa);

        const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
        camera.position.set(3, 3, 3);
        camera.lookAt(0, 0, 0);

        const renderer = new THREE.WebGLRenderer({ 
          canvas: canvasRef.current,
          antialias: true,
          alpha: true
        });
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.physicallyCorrectLights = true;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;
        
        // Get actual container size
        const container = canvasRef.current.parentElement;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        
        renderer.setSize(containerWidth, containerHeight);
        camera.aspect = containerWidth / containerHeight;
        camera.updateProjectionMatrix();

        // Enhanced lighting setup for better curvature visibility
        // Ambient light for overall illumination (softer)
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
        scene.add(ambientLight);
        
        // Key light (main directional light from top-right)
        const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
        keyLight.position.set(5, 8, 5);
        keyLight.castShadow = true;
        keyLight.shadow.mapSize.width = 1024;
        keyLight.shadow.mapSize.height = 1024;
        scene.add(keyLight);
        
        // Fill light (softer light from opposite side to reduce harsh shadows)
        const fillLight = new THREE.DirectionalLight(0xffffff, 0.6);
        fillLight.position.set(-5, 3, -5);
        scene.add(fillLight);
        
        // Back light (rim light to highlight edges and contours)
        const backLight = new THREE.DirectionalLight(0xffffff, 0.4);
        backLight.position.set(0, 5, -8);
        scene.add(backLight);

        // Grid
        const gridHelper = new THREE.GridHelper(3, 10, 0xdddddd, 0xeeeeee);
        scene.add(gridHelper);

        // Load file
        const fileExtension = fileObj.name.toLowerCase().split('.').pop();
        const arrayBuffer = await fileObj.arrayBuffer();
        let geometry = null;

        if (fileExtension === 'stl') {
          const stlLoader = new STLLoader();
          geometry = stlLoader.parse(arrayBuffer);
        } else if (fileExtension === 'obj') {
          const objLoader = new OBJLoader();
          const textContent = new TextDecoder().decode(arrayBuffer);
          const objGroup = objLoader.parse(textContent);
          if (objGroup.children.length > 0) {
            geometry = objGroup.children[0].geometry;
          }
        } else if (fileExtension === 'ply') {
          const plyLoader = new PLYLoader();
          geometry = plyLoader.parse(arrayBuffer);
        } else if (fileExtension === '3mf') {
          // Use server conversion with fallback
          try {
            geometry = await convertFileToSTL(fileObj, '3mf');
          } catch (conversionError) {
            console.warn('3MF conversion failed, showing fallback preview:', conversionError.message);
            throw new Error('3MF file preview is temporarily unavailable due to conversion service issues. The file is valid and ready for printing.');
          }
        } else if (fileExtension === 'step' || fileExtension === 'stp') {
          // Use server conversion for STEP files (can take a few minutes)
          setIsStepFile(true);
          geometry = await convertFileToSTL(fileObj, 'step');
        } else if (fileExtension === 'amf') {
          throw new Error('AMF preview coming soon! Your file was uploaded successfully and will be analyzed.');
        } else {
          throw new Error(`${fileExtension.toUpperCase()} preview not available yet - file uploaded successfully for analysis`);
        }

        if (!geometry) {
          throw new Error('Could not parse 3D geometry');
        }

        // Create and add mesh with enhanced material for better curvature visibility
        const material = new THREE.MeshPhongMaterial({ 
          color: 0x4A90E2,
          shininess: 80,        // Higher shininess for more visible highlights
          specular: 0x888888,   // Brighter specular highlights for better contrast
          flatShading: false,   // Smooth shading to show curves
          side: THREE.DoubleSide // Render both sides
        });
        const mesh = new THREE.Mesh(geometry, material);
        
        // Enable shadows for better depth perception
        mesh.castShadow = true;
        mesh.receiveShadow = true;

        // Center and scale
        geometry.computeBoundingBox();
        geometry.computeVertexNormals(); // Ensure smooth normals are computed
        const box = geometry.boundingBox;
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        geometry.translate(-center.x, -center.y, -center.z);
        const maxDim = Math.max(size.x, size.y, size.z);
        if (maxDim > 0) mesh.scale.setScalar(2 / maxDim);

        scene.add(mesh);

        // Analysis - Calculate ACTUAL volume and surface area from geometry
        const rawVolume = calculateMeshVolume(geometry);
        const triangleCount = geometry.attributes.position.count / 3;
        const rawSurfaceArea = calculateMeshSurfaceArea(geometry);
        
        // Convert to cm³ and cm² based on file units
        const volume = convertVolumeToCm3(rawVolume, units);
        const surfaceArea = convertAreaToCm2(rawSurfaceArea, units);
        
        // Log actual calculations
        console.log(`Mesh Analysis (${units}): Raw Volume=${rawVolume.toFixed(4)}, Converted Volume=${volume.toFixed(4)} cm³`);
        console.log(`Mesh Analysis (${units}): Raw Surface Area=${rawSurfaceArea.toFixed(4)}, Converted Surface Area=${surfaceArea.toFixed(4)} cm²`);
        
        // Calculate complexity as percentage (1%-100%)
        // Match backend calculation exactly
        let geometryFactor = 0.01;
        
        if (triangleCount < 100) {
          geometryFactor = 0.01; // Very simple: 1%
        } else if (triangleCount < 500) {
          geometryFactor = 0.01 + (triangleCount / 500) * 0.09; // 1-10%
        } else if (triangleCount < 2000) {
          geometryFactor = 0.10 + ((triangleCount - 500) / 1500) * 0.20; // 10-30%
        } else if (triangleCount < 5000) {
          geometryFactor = 0.30 + ((triangleCount - 2000) / 3000) * 0.30; // 30-60%
        } else if (triangleCount < 10000) {
          geometryFactor = 0.60 + ((triangleCount - 5000) / 5000) * 0.25; // 60-85%
        } else {
          geometryFactor = Math.min(1.0, 0.85 + ((triangleCount - 10000) / 10000) * 0.15); // 85-100%
        }
        
        // Factor 2: Surface complexity based on surface-to-volume ratio
        let surfaceFactor = 0.50; // Default to medium
        
        if (surfaceArea > 0 && volume > 0) {
          const surfaceToVolume = surfaceArea / volume;
          
          if (surfaceToVolume < 10) {
            surfaceFactor = 0.01; // Simple geometry
          } else if (surfaceToVolume < 30) {
            surfaceFactor = 0.10 + ((surfaceToVolume - 10) / 20) * 0.20; // 10-30%
          } else if (surfaceToVolume < 60) {
            surfaceFactor = 0.30 + ((surfaceToVolume - 30) / 30) * 0.30; // 30-60%
          } else {
            surfaceFactor = Math.min(1.0, 0.60 + ((surfaceToVolume - 60) / 40) * 0.40); // 60-100%
          }
        }
        
        // Combine factors (weighted average: 60% geometry, 40% surface) - matches backend
        let complexityPercentage = (geometryFactor * 0.6) + (surfaceFactor * 0.4);
        
        // Ensure within bounds [0.01, 1.0]
        complexityPercentage = Math.max(0.01, Math.min(1.0, complexityPercentage));

        const analysis = {
          rawVolume,  // Store raw volume for unit conversion
          rawSurfaceArea,  // Store raw surface area for unit conversion
          volume,
          surfaceArea,
          complexity: complexityPercentage, // Now stored as 0.01-1.0 (1%-100%)
          triangleCount,
          fileSize: fileObj.size,
          dimensions: {
            x: size.x.toFixed(2),
            y: size.y.toFixed(2),
            z: size.z.toFixed(2)
          }
        };

        setFileInfo(analysis);
        setPreviewState('success');
        setIsStepFile(false); // Reset STEP file flag on success
        if (onAnalysis) onAnalysis(analysis);

        // Simple controls
        let isDragging = false;
        let previousMouse = { x: 0, y: 0 };

        const onMouseDown = (e) => {
          isDragging = true;
          previousMouse = { x: e.clientX, y: e.clientY };
        };

        const onMouseMove = (e) => {
          if (!isDragging) return;
          const deltaX = e.clientX - previousMouse.x;
          const deltaY = e.clientY - previousMouse.y;
          
          const spherical = new THREE.Spherical();
          spherical.setFromVector3(camera.position);
          spherical.theta -= deltaX * 0.01;
          spherical.phi += deltaY * 0.01;
          spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, spherical.phi));
          
          camera.position.setFromSpherical(spherical);
          camera.lookAt(0, 0, 0);
          
          previousMouse = { x: e.clientX, y: e.clientY };
        };

        const onMouseUp = () => {
          isDragging = false;
        };

        const onWheel = (e) => {
          const distance = camera.position.length();
          const newDistance = distance * (1 + e.deltaY * 0.001);
          const clamped = Math.max(1, Math.min(10, newDistance));
          camera.position.normalize().multiplyScalar(clamped);
          e.preventDefault();
        };

        const canvas = canvasRef.current;
        canvas.addEventListener('mousedown', onMouseDown);
        canvas.addEventListener('mousemove', onMouseMove);
        canvas.addEventListener('mouseup', onMouseUp);
        canvas.addEventListener('wheel', onWheel);

        // Render loop
        const animate = () => {
          requestAnimationFrame(animate);
          renderer.render(scene, camera);
        };
        animate();

        // Cleanup
        return () => {
          canvas?.removeEventListener('mousedown', onMouseDown);
          canvas?.removeEventListener('mousemove', onMouseMove);
          canvas?.removeEventListener('mouseup', onMouseUp);
          canvas?.removeEventListener('wheel', onWheel);
          renderer.dispose();
        };

      } catch (error) {
        console.error('3D Error:', error);
        
        // Reset STEP file flag
        setIsStepFile(false);
        
        // Create user-friendly error message
        const fileExtension = fileObj.name.toLowerCase().split('.').pop();
        let userMessage = '';
        if (error.message.includes('Server conversion failed')) {
          userMessage = 'Preview temporarily unavailable due to server processing. Your file was uploaded successfully and will be analyzed for pricing.';
        } else if (error.message.includes('not available') || error.message.includes('coming soon')) {
          userMessage = error.message;
        } else {
          userMessage = `Preview not available for this file type. Your ${fileExtension?.toUpperCase() || 'file'} was uploaded successfully and will be analyzed for accurate pricing.`;
        }
        
        setErrorMessage(userMessage);
        setPreviewState('error');
        
        // Provide fallback analysis so quote can continue
        const fallback = {
          volume: Math.max(1.0, fileObj.size / 100000),
          surfaceArea: Math.max(10.0, fileObj.size / 50000),
          complexity: 1.5,
          fileSize: fileObj.size
        };
        if (onAnalysis) onAnalysis(fallback);
      }
    };

  return (
    <div className={`relative ${className}`}>
      <div className="w-full h-[600px] bg-gray-100 rounded-lg border border-gray-200 overflow-hidden relative">
        <canvas 
          ref={canvasRef}
          className="w-full h-full block"
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
        
        {previewState === 'idle' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 text-xl">📦</span>
              </div>
              <p className="text-gray-600">Upload a 3D file to see preview</p>
            </div>
          </div>
        )}
        
        {previewState === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-50/90 z-10">
            <div className="text-center max-w-md px-6">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-blue-600 font-medium mb-2">
                {isStepFile ? 'Loading STEP file preview...' : 'Loading 3D preview...'}
              </p>
              {isStepFile && (
                <div className="bg-blue-100 rounded-lg p-4 border border-blue-200 mt-3">
                  <p className="text-sm text-blue-800">
                    ⏱️ <strong>STEP files may take a few minutes to process.</strong>
                  </p>
                  <p className="text-xs text-blue-700 mt-2">
                    Our system is processing your STEP/STP file for preview. Please wait...
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {previewState === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-yellow-50/95 z-10">
            <div className="text-center max-w-sm p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 text-2xl">📁</span>
              </div>
              <h3 className="font-semibold text-slate-900 mb-3">3D Preview Unavailable</h3>
              <p className="text-sm text-slate-600 mb-4">{errorMessage}</p>
              <div className="bg-green-100 rounded-lg p-4 border border-green-200">
                <div className="flex items-center space-x-2">
                  <span className="text-green-600 text-lg">✓</span>
                  <div className="text-left">
                    <p className="text-sm font-medium text-green-800">File Upload Successful</p>
                    <p className="text-xs text-green-700">Continue with quote parameters below</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {previewState === 'success' && (
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm z-10">
            <div className="flex items-center space-x-3 text-sm">
              <span className="text-green-600">✓ 3D Preview</span>
              <span className="text-gray-500">|</span>
              <span className="text-blue-600">🖱️ Drag • 🔍 Scroll</span>
            </div>
          </div>
        )}
      </div>

      {fileInfo && (
        <div className="mt-4 bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-3">3D Model Analysis</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {/* Basic Geometry */}
            <div>
              <span className="text-blue-700 font-medium">Volume:</span>
              <span className="ml-2 text-blue-900">{fileInfo.volume.toFixed(2)} cm³</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Surface Area:</span>
              <span className="ml-2 text-blue-900">{fileInfo.surfaceArea.toFixed(2)} cm²</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Volume/Area Ratio:</span>
              <span className="ml-2 text-blue-900">{(fileInfo.volume / fileInfo.surfaceArea).toFixed(4)}</span>
            </div>
            
            {/* Material Properties - TODO: HIDE BEFORE DEPLOYMENT */}
            {material && strength && (
              <>
                <div>
                  <span className="text-blue-700 font-medium">Required Weight:</span>
                  <span className="ml-2 text-blue-900">{calculateMaterialWeight(fileInfo.volume, material, strength)?.toFixed(2) || 'N/A'} g</span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Infill:</span>
                  <span className="ml-2 text-blue-900">{(getInfillPercentage(strength) * 100).toFixed(0)}%</span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Material Density:</span>
                  <span className="ml-2 text-blue-900">{getMaterialDensity(material).toFixed(2)} g/cm³</span>
                </div>
              </>
            )}
            
            {/* Adjustment Factors - TODO: HIDE BEFORE DEPLOYMENT */}
            {material && strength && quality && (
              <>
                <div>
                  <span className="text-blue-700 font-medium">Weight Adj Factor:</span>
                  <span className="ml-2 text-blue-900">{calculateWeightAdjustmentFactor(fileInfo.volume / fileInfo.surfaceArea).toFixed(4)}</span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Actual Print Weight:</span>
                  <span className="ml-2 text-blue-900">
                    {(() => {
                      const reqWeight = calculateMaterialWeight(fileInfo.volume, material, strength);
                      const weightAdj = calculateWeightAdjustmentFactor(fileInfo.volume / fileInfo.surfaceArea);
                      return (reqWeight * weightAdj).toFixed(2);
                    })()} g
                  </span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Quality Adj Factor:</span>
                  <span className="ml-2 text-blue-900">{getQualityAdjustmentFactor(quality).toFixed(2)}x</span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Cost Adj Factor:</span>
                  <span className="ml-2 text-blue-900">
                    {(() => {
                      const reqWeight = calculateMaterialWeight(fileInfo.volume, material, strength);
                      const weightAdj = calculateWeightAdjustmentFactor(fileInfo.volume / fileInfo.surfaceArea);
                      const actualWeight = reqWeight * weightAdj;
                      return calculateCostAdjustmentFactor(actualWeight).toFixed(4);
                    })()}
                  </span>
                </div>
                <div>
                  <span className="text-blue-700 font-medium">Complexity Adj Factor:</span>
                  <span className="ml-2 text-blue-900">{calculateComplexityAdjustmentFactor(fileInfo.complexity).toFixed(4)}</span>
                </div>
              </>
            )}
            
            {/* Complexity and File Info */}
            <div>
              <span className="text-blue-700 font-medium">Complexity:</span>
              <span className="ml-2 text-blue-900">{(fileInfo.complexity * 100).toFixed(0)}%</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">Triangles:</span>
              <span className="ml-2 text-blue-900">{Math.round(fileInfo.triangleCount || 0).toLocaleString()}</span>
            </div>
            <div>
              <span className="text-blue-700 font-medium">File Size:</span>
              <span className="ml-2 text-blue-900">{(fileInfo.fileSize / 1024 / 1024).toFixed(1)} MB</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreeDPreview;